import { TRUSTARC_DIV_ID } from 'constants/HTMLAttributes';

export interface TrustArcConfig {
  enabled: boolean;
  domain: string;
}

export interface TrustArcContext {
  trustArcConfig?: TrustArcConfig;
  locale: string;
  supportedLanguages: readonly string[];
  isBot: boolean;
  isBlogWidget: boolean;
  isEndToEndTest: boolean;
  isDevelopment: boolean;
}

/**
 * Service for managing TrustArc cookie consent banner integration.
 */
export class TrustArcService {
  /**
   * Determines if TrustArc should be loaded based on context.
   * 
   * @param context - Context information for determining if TrustArc should load
   * @returns true if TrustArc should be loaded
   */
  static shouldLoad(context: TrustArcContext): boolean {
    const { trustArcConfig, isBot, isBlogWidget, isEndToEndTest, isDevelopment } = context;
    
    return (
      trustArcConfig?.enabled === true &&
      !isBot &&
      !isBlogWidget &&
      !isEndToEndTest &&
      !isDevelopment
    );
  }

  /**
   * Generates the TrustArc script URL with proper language support.
   * Returns empty string if TrustArc should not be loaded or domain is not provided.
   * 
   * @param context - Context information for generating the script URL
   * @returns Complete TrustArc script URL with language parameter, or empty string
   */
  static getScriptUrl(context: TrustArcContext): string {
    if (!this.shouldLoad(context)) {
      return '';
    }

    const { trustArcConfig, locale, supportedLanguages } = context;
    const domain = trustArcConfig?.domain || '';
    
    if (!domain) {
      return '';
    }
    
    const language = this.getLanguageCode(locale, supportedLanguages);
    return `https://consent.trustarc.com/notice?domain=${domain}&c=${TRUSTARC_DIV_ID}&js=nj&noticeType=bb&text=true&gtm=1&language=${language}`;
  }

  /**
   * Extracts the base language code from a locale.
   * Maps site locale to TrustArc language code (ISO 639-1 format).
   * 
   * Examples:
   * - 'en-CA' -> 'en'
   * - 'fr-CA' -> 'fr'
   * - 'es' -> 'es'
   * 
   * @param locale - Site locale (e.g., 'en-CA', 'fr-CA', 'es')
   * @param supportedLanguages - Array of languages supported by the tenant
   * @returns TrustArc language code (e.g., 'en', 'fr', 'es')
   */
  static getLanguageCode(locale: string, supportedLanguages: readonly string[]): string {
    // Check if the locale is supported by the tenant
    if (!supportedLanguages.includes(locale)) {
      // Default to first supported language or 'en'
      return this.extractBaseLanguage(supportedLanguages[0] || 'en');
    }
    
    return this.extractBaseLanguage(locale);
  }

  /**
   * Extracts the base language code from a locale string.
   * 
   * @param locale - Locale string (e.g., 'en-CA', 'fr', 'es')
   * @returns Base language code (e.g., 'en', 'fr', 'es')
   */
  private static extractBaseLanguage(locale: string): string {
    // Split by hyphen and take the first part (language code)
    return locale.split('-')[0];
  }
}

